// 인증 관련 유틸리티 함수들

/**
 * 비밀번호를 SHA-256으로 해시화
 * @param {string} password - 해시화할 비밀번호
 * @returns {Promise<string>} - 해시된 비밀번호
 */
export const hashPassword = async (password) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

/**
 * 비밀번호 검증
 * @param {string} inputPassword - 입력된 비밀번호
 * @returns {Promise<boolean>} - 검증 결과
 */
export const validatePassword = async (inputPassword) => {
  // 환경변수에서 저장된 해시값 가져오기
  const storedHash = import.meta.env.VITE_ADMIN_PASSWORD_HASH;
  
  if (!storedHash) {
    console.warn('환경변수에 비밀번호 해시가 설정되지 않았습니다.');
    return false;
  }

  // 입력된 비밀번호를 해시화
  const inputHash = await hashPassword(inputPassword);
  
  // 해시값 비교
  return inputHash === storedHash;
};

/**
 * 인증 토큰 생성
 * @returns {string} - 생성된 토큰
 */
export const generateAuthToken = () => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2);
  const sessionId = Math.random().toString(36).substring(2);
  return btoa(`${timestamp}-${random}-${sessionId}`);
};

/**
 * 인증 토큰 검증
 * @param {string} token - 검증할 토큰
 * @returns {boolean} - 토큰 유효성
 */
export const validateAuthToken = (token) => {
  if (!token) return false;
  
  try {
    const decoded = atob(token);
    const [timestamp] = decoded.split('-');
    const tokenTime = parseInt(timestamp);
    const currentTime = Date.now();
    
    // 토큰 만료 시간: 8시간 (28800000ms)
    const TOKEN_EXPIRY = 8 * 60 * 60 * 1000;
    
    return (currentTime - tokenTime) < TOKEN_EXPIRY;
  } catch (error) {
    console.error('Token validation error:', error);
    return false;
  }
};

/**
 * 세션 스토리지에서 인증 토큰 가져오기
 * @returns {string|null} - 인증 토큰 또는 null
 */
export const getAuthToken = () => {
  return sessionStorage.getItem('authToken');
};

/**
 * 세션 스토리지에 인증 토큰 저장
 * @param {string} token - 저장할 토큰
 */
export const setAuthToken = (token) => {
  sessionStorage.setItem('authToken', token);
};

/**
 * 인증 토큰 제거
 */
export const removeAuthToken = () => {
  sessionStorage.removeItem('authToken');
};

/**
 * 현재 사용자가 인증되었는지 확인
 * @returns {boolean} - 인증 상태
 */
export const isAuthenticated = () => {
  const token = getAuthToken();
  return validateAuthToken(token);
};

/**
 * 비밀번호 해시 생성기 (개발용)
 * 실제 비밀번호를 해시로 변환하는 함수
 * @param {string} password - 원본 비밀번호
 * @returns {Promise<string>} - 해시된 비밀번호
 */
export const createPasswordHash = async (password) => {
  const hash = await hashPassword(password);
  console.log(`비밀번호 "${password}"의 해시값: ${hash}`);
  console.log('환경변수에 다음과 같이 설정하세요:');
  console.log(`VITE_ADMIN_PASSWORD_HASH=${hash}`);
  return hash;
};
